import GenericBody from "../Componentes/GenericBody";
import NavBar from "../Componentes/NavBar";
import Overlay from "../Componentes/Overlay";
import SideMenu from "../Componentes/SideMenu";
import SideMenuBtn from "../Componentes/SideMenuBtn";
import Content from "../Modulos/AtendimentoAmb/Home/Content";
import ModalNovoAtendimento from "../Modulos/AtendimentoAmb/Home/ModalNovoAtendimento";
import ModalGerenciarQueixas from "../Modulos/AtendimentoAmb/Home/ModalGerenciarQueixas";
import { useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModal } from "../contexts/ModalContext";
import { useToast } from "../contexts/ToastContext";
import { AuthContext } from "../Auth/AuthProvider";
import { VerifyPermission } from "../Funcoes/VerifyPermission";
import useApi from "../hooks/useApi";

const PERMISSAO_BASE = { modulo: "ssma", submodulo: "atendimento-amb" };

const AtendimentoAmb = () => {

    const { openModal, closeModal, isModalOpen } = useModal();
    const { user } = useContext(AuthContext);
    const { notifyError } = useToast();
    const api = useApi();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const [listRefreshKey, setListRefreshKey] = useState(0);
    const [loadingRelatorio, setLoadingRelatorio] = useState(false);

    const podeLancarEditar = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "lancar-editar" }),
        [user]
    );

    const podeGerenciarQueixas = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "gerenciar-queixas" }),
        [user]
    );

    const podeAcessarAtestados = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "atestados-access" }),
        [user]
    );

    const podeAcessarTestes = useMemo(
        () => VerifyPermission(user, { ...PERMISSAO_BASE, funcao: "testes-alcool-drogas-access" }),
        [user]
    );

    const openSidebar = () => openModal("atendimentoAmbSidebar");

    const openNovoAtendimento = () => {
        if (!podeLancarEditar) return;
        closeModal();
        openModal("atendimentoAmbNovo");
    };

    const openGerenciarQueixas = () => {
        if (!podeGerenciarQueixas) return;
        closeModal();
        openModal("atendimentoAmbQueixas");
    };

    const openAtestados = () => {
        if (!podeAcessarAtestados) return;
        closeModal();
        navigate("/modulo/ssma/atendimento-amb/atestados");
    };

    const openTestesAlcoolDrogas = () => {
        if (!podeAcessarTestes) return;
        closeModal();
        navigate("/modulo/ssma/atendimento-amb/testes-alcool-drogas");
    };

    const gerarRelatorio = async () => {
        if (loadingRelatorio) return;

        const filtrados = contentRef.current?.getFilteredAtendimentos?.() ?? [];

        if (filtrados.length === 0) {
            notifyError("Nenhum atendimento encontrado com os filtros atuais.");
            return;
        }

        setLoadingRelatorio(true);

        try {
            const response = await api.post(
                "/Painel/API/AtendimentoAmb/GenerateRelatorio/index.php",
                filtrados,
                { responseType: "blob" }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "").slice(0, 14);
            link.href = url;
            link.setAttribute("download", `atendimentos_ambulatoriais_${stamp}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            let msg = "Erro ao gerar relatório.";
            if (err?.response?.data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await err.response.data.text());
                    if (parsed?.message) msg = parsed.message;
                } catch {
                    /* mantém mensagem padrão */
                }
            } else if (err?.response?.data?.message) {
                msg = err.response.data.message;
            }
            notifyError(msg);
        } finally {
            setLoadingRelatorio(false);
        }
    };

    return (
        <>
            <NavBar
                title="ATENDIMENTO AMBULATORIAL"
                goBackOnePage
                setSideBar={openSidebar}
            />
            <GenericBody>
                <Content ref={contentRef} refreshKey={listRefreshKey} />
            </GenericBody>

            {isModalOpen("atendimentoAmbSidebar") && (
                <Overlay state changeContent={closeModal}>
                    <SideMenu>
                        {podeLancarEditar && (
                            <SideMenuBtn button="NOVO ATENDIMENTO" clickFunc={openNovoAtendimento} />
                        )}
                        {podeGerenciarQueixas && (
                            <SideMenuBtn button="GERENCIAR QUEIXAS" clickFunc={openGerenciarQueixas} />
                        )}
                        {podeAcessarAtestados && (
                            <SideMenuBtn button="ATESTADOS" clickFunc={openAtestados} />
                        )}
                        {podeAcessarTestes && (
                            <SideMenuBtn button="TESTES ALCOOL E DROGAS" clickFunc={openTestesAlcoolDrogas} />
                        )}
                        <SideMenuBtn
                            button="GERAR RELATÓRIO"
                            clickFunc={gerarRelatorio}
                            isLoading={loadingRelatorio}
                        />
                    </SideMenu>
                </Overlay>
            )}

            {podeGerenciarQueixas && isModalOpen("atendimentoAmbQueixas") && (
                <ModalGerenciarQueixas />
            )}

            {podeLancarEditar && isModalOpen("atendimentoAmbNovo") && (
                <ModalNovoAtendimento onSaved={() => setListRefreshKey((k) => k + 1)} />
            )}
        </>
    );
};

export default AtendimentoAmb;
